import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useState } from "react";

// API's
import { updateAdministrationTools } from "@/Settings/api/AdministrationTools/mutations";

// MUI
import { Box, Typography, Stack } from "@mui/material";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// PropTypes
import PropTypes from "prop-types";

const UPPER_LIMIT = 101;
const allSources = Array.from({length: UPPER_LIMIT}).map((_, idx) =>
	idx === 0 ? { label: String(idx), value: String(idx) } : { label: idx, value: idx }
);

const sourcesLabels = [
	{ label: "Cash Receipts", value: "-------" },
	{ label: "New Cars JRNL Entries", value: "-------" },
	{ label: "Used Cars JRNL Entries", value: "-------" },
	{ label: "Manual Entries", value: "-------" },
	{ label: "Cash Disbursements", value: "-------" },
	{ label: "Payroll Entries", value: "-------" },
];

export default function Sources(props) {
	const queryClient = useQueryClient();

	const location = useSelector((state) => state.location.location);
	const locationId = location?.locationId;
	const [sourceUpdating, setSourceUpdating] = useState(null);

	// Modify the data in the DB
	const { mutate: mutateSources, isPending: isLoading } = useMutation({
		mutationKey: ["administrationToolsSourcesMutation", locationId],
		mutationFn: (input) => updateAdministrationTools(locationId, input),
		onError: (error) => {
			errorLogger(error.message);
			setSourceUpdating(null);
		},
		onSuccess: (data) => {
			queryClient.setQueryData(["administrationTools", locationId], (cachedData) => {
				return {
					...cachedData,
					integrations: {
						...cachedData?.integrations,
						dmsSources: data?.integrations?.dmsSources,
					},
				};
			});
			setSourceUpdating(null);
		},
	});

	const getSources = () => props.settings?.integrations?.dmsSources ?? [];

	const getAvailableValues = () => {
		const chosenOptions = getSources()?.map((source) => parseInt(source.value, 10));
		return [...allSources].filter((source) => !chosenOptions?.includes(parseInt(source.value, 10)));
	};

	const handleChange = (selectedValue, label) => {
		setSourceUpdating(label);

		const dmsSourcesProps = getSources();
		const dmsSources = dmsSourcesProps.length ? dmsSourcesProps : sourcesLabels;
		const updatedDMSSources = dmsSources?.map((source) =>
			source.label === label ? { ...source, value: selectedValue.value.toString() } : source
		);

		const input = {
			integrations: {
				...props?.settings?.integrations,
				dmsSources: updatedDMSSources,
			},
		};

		mutateSources(input);
	};

	const isLoadingSaveSources = (s) => isLoading && sourceUpdating === s;

	const sameProperties = (source) => ({
		loading: isLoadingSaveSources(source.label),
		readOnly: isLoadingSaveSources(source.label),
		disabled: isLoading,
		options: [{ label: "-------", value: "-------" }, source.value !== "-------" && source, ...getAvailableValues()],
	});

	const getContent = () => {
		const fetchedSources = getSources() ?? [];
		const sources = fetchedSources.length ? fetchedSources : sourcesLabels;

		return sources.map((source, idx) => (
			<Stack
				mt={"11px"}
				display={"flex"}
				key={`${source.label}_${idx}`}
				direction={"row"}
				alignItems={"center"}
				justifyContent={"space-between"}
			>
				<Typography className="!font-sans !text-[14.5px] text-default">{source.label}</Typography>
				<Box>
					<SmartSelector
						selectBy={"value"}
						showBy={"value"}
						selected={fetchedSources?.[idx] ?? null}
						inputProps={{
							placeholder: "Select Source",
						}}
						sx={{ minWidth: "90px" }}
						onChangeAction={(option) => handleChange(option, source.label)}
						{...sameProperties(source)}
					/>
				</Box>
			</Stack>
		));
	};

	return (
		<Stack display={"flex"} direction={"column"} width={258} className="!my-[32px]">
			<Typography className="!font-montserrat !text-default !text-[20px] !font-medium">Sources</Typography>
			<Stack display={"flex"} direction={"column"}>
				{ getContent() }
			</Stack>
		</Stack>
	);
}

Sources.propTypes = {
	settings: PropTypes.object,
};
